import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serviceRequestAPI, customerAPI, broadbandAPI, catvAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ClipboardList, Plus, PowerOff, Power, Repeat, MapPin, PenLine } from 'lucide-react';
import SignaturePad from '../../components/agent/SignaturePad';
import SearchableSelect from '../../components/common/SearchableSelect';
import DtdPlanChangeNotice from '../../components/customer/DtdPlanChangeNotice';

const TYPE_LABEL = {
  disconnection: 'Disconnection', reconnection: 'Reconnection',
  package_change: 'Package / Plan Change', relocation: 'Relocation',
};
const TYPE_ICON = { disconnection: PowerOff, reconnection: Power, package_change: Repeat, relocation: MapPin };
const STATUS_COLOR = { pending: '#d97706', scheduled: '#d97706', completed: '#059669', rejected: '#dc2626', superseded: '#6b7280' };
const DISCONNECTION_REASONS = ['JIO', 'Airtel', 'DTH', 'GTPL Broadband', 'Shifting', 'Vacation', 'Studies & Exam'];

export default function CustomerRequests() {
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]); // my own record + linked account, if any
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');
  const [selected, setSelected] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ accountId: '', type: 'disconnection', serviceType: 'broadband', reason: '', requestedPlan: '', requestedPackage: '' });
  const [newAddress, setNewAddress] = useState({ line1: '', area: '', city: '', pincode: '' });
  const [signature, setSignature] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [dtdNoticeOpen, setDtdNoticeOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: me }, { data: reqs }] = await Promise.all([
        customerAPI.getMe(),
        serviceRequestAPI.getAll({ limit: 20 }),
      ]);
      const list = [me.data, ...(me.data.linkedAccount ? [me.data.linkedAccount] : [])];
      setAccounts(list);
      setForm(f => ({ ...f, accountId: f.accountId || list[0]._id, serviceType: list[0].customerType === 'catv' ? 'catv' : 'broadband' }));
      setRequests(reqs.data || []);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (form.type === 'package_change' && form.serviceType === 'broadband' && plans.length === 0) {
      broadbandAPI.getPlans({ activeOnly: 'true' }).then(r => setPlans(r.data?.data || [])).catch(() => {});
    }
    if (form.type === 'package_change' && form.serviceType === 'catv' && packages.length === 0) {
      catvAPI.getPackages({ activeOnly: 'true' }).then(r => setPackages(r.data?.data || [])).catch(() => {});
    }
  }, [form.type, form.serviceType, plans.length, packages.length]);

  const account = accounts.find(a => a._id === form.accountId);
  const serviceOptions = !account ? ['broadband', 'catv'] : account.customerType === 'both' ? ['broadband', 'catv'] : [account.customerType];
  // See DtdPlanChangeNotice for why DTD CATV customers can't submit this request type.
  const isDtdCatvPackageChange = form.type === 'package_change' && form.serviceType === 'catv' && account?.billing?.durationType === 'dtd';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDtdCatvPackageChange) { setDtdNoticeOpen(true); return; }
    if (!signature) { toast.error('Please sign to confirm this request'); return; }
    setSubmitting(true);
    try {
      const payload = { customer: form.accountId, type: form.type, reason: form.reason, signature };
      if (['disconnection', 'reconnection', 'package_change'].includes(form.type)) payload.serviceType = form.serviceType;
      if (form.type === 'package_change') {
        if (form.serviceType === 'broadband') payload.requestedPlan = form.requestedPlan;
        else payload.requestedPackage = form.requestedPackage;
      }
      if (form.type === 'relocation') payload.newAddress = newAddress;

      const { data } = await serviceRequestAPI.create(payload);
      toast.success(`Request ${data.data.ticketNumber} submitted! Our team will review it soon.`);
      setShowNew(false);
      setForm(f => ({ ...f, type: 'disconnection', reason: '', requestedPlan: '', requestedPackage: '' }));
      setNewAddress({ line1: '', area: '', city: '', pincode: '' });
      setSignature(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Service Requests</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Disconnect, reconnect, change your package, or move address</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={14} /> New Request
        </button>
      </div>

      {showNew && (
        <div className="card mb-6">
          <div className="card-header">
            <h3>Submit a Request</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNew(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {accounts.length > 1 && (
                <div className="form-group">
                  <label className="form-label">Account</label>
                  <select className="form-control" value={form.accountId} onChange={e => setForm(f => ({ ...f, accountId: e.target.value }))}>
                    {accounts.map(a => (
                      <option key={a._id} value={a._id}>{a.name} — {a.customerType === 'catv' ? 'CableTV' : a.customerType === 'broadband' ? 'Broadband' : 'Combo'} ({a.customerId})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Request Type *</label>
                  <select className="form-control" value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value, requestedPlan: '', requestedPackage: '', reason: '' }))}>
                    <option value="disconnection">Disconnection</option>
                    <option value="reconnection">Reconnection</option>
                    <option value="package_change">Package / Plan Change</option>
                    <option value="relocation">Relocation</option>
                  </select>
                </div>
                {['disconnection', 'reconnection', 'package_change'].includes(form.type) && (
                  <div className="form-group">
                    <label className="form-label">Service</label>
                    <select className="form-control" value={form.serviceType}
                      onChange={e => setForm(f => ({ ...f, serviceType: e.target.value, requestedPlan: '', requestedPackage: '' }))}>
                      {serviceOptions.map(s => <option key={s} value={s}>{s === 'broadband' ? 'Broadband' : 'CableTV'}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {form.type === 'package_change' && form.serviceType === 'broadband' && (
                <div className="form-group">
                  <label className="form-label">Requested Plan *</label>
                  <SearchableSelect
                    value={form.requestedPlan}
                    onChange={v => setForm(f => ({ ...f, requestedPlan: v }))}
                    placeholder="Search plan…"
                    options={plans.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))}
                  />
                </div>
              )}
              {form.type === 'package_change' && form.serviceType === 'catv' && (
                isDtdCatvPackageChange ? (
                  <div className="form-group">
                    <div style={{ background: '#dbeafe', color: '#1e3a8a', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem' }}>
                      Since you are a DTD (Date to Date) customer with advance renewal, you can select the package on your next renewal.
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <label className="form-label">Requested Package *</label>
                    <SearchableSelect
                      value={form.requestedPackage}
                      onChange={v => setForm(f => ({ ...f, requestedPackage: v }))}
                      placeholder="Search package…"
                      options={packages.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}` }))}
                    />
                  </div>
                )
              )}

              {form.type === 'relocation' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">New Address *</label>
                    <input className="form-control" value={newAddress.line1} onChange={e => setNewAddress(a => ({ ...a, line1: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area</label>
                    <input className="form-control" value={newAddress.area} onChange={e => setNewAddress(a => ({ ...a, area: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input className="form-control" value={newAddress.city} onChange={e => setNewAddress(a => ({ ...a, city: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <input className="form-control" value={newAddress.pincode} onChange={e => setNewAddress(a => ({ ...a, pincode: e.target.value }))} />
                  </div>
                </div>
              )}

              {form.type === 'disconnection' ? (
                <div className="form-group">
                  <label className="form-label">Reason *</label>
                  <select className="form-control" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} required>
                    <option value="">Select reason…</option>
                    {DISCONNECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Reason / Notes</label>
                  <textarea className="form-control" rows={3} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                    placeholder="Let us know why, or any details we should have" />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Signature *</label>
                {signature ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 6, background: '#fff' }}>
                      <img src={signature} alt="signature" style={{ height: 44, objectFit: 'contain' }} />
                    </div>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSignOpen(true)}>Re-sign</button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-secondary" onClick={() => setSignOpen(true)}>
                    <PenLine size={14} /> Sign to Confirm
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {signOpen && (
        <SignaturePad value={signature} onSave={setSignature} onClose={() => setSignOpen(false)} />
      )}

      {dtdNoticeOpen && (
        <DtdPlanChangeNotice onClose={() => setDtdNoticeOpen(false)} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : requests.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <ClipboardList size={32} />
          <h3>No requests yet</h3>
          <p>Need to disconnect, change your package, or move? Click "New Request".</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {requests.map(r => {
            const Icon = TYPE_ICON[r.type] || ClipboardList;
            return (
              <div key={r._id} className="card" style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === r._id ? null : r._id)}>
                <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{r.ticketNumber}</span>
                      <span style={{ fontSize: '.72rem', padding: '2px 8px', borderRadius: 12, fontWeight: 600, background: `${STATUS_COLOR[r.status]}20`, color: STATUS_COLOR[r.status], textTransform: 'capitalize' }}>
                        {r.status}
                      </span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon size={14} /> {TYPE_LABEL[r.type]}
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                      {r.serviceType && <span style={{ textTransform: 'capitalize' }}>{r.serviceType === 'broadband' ? 'Broadband' : 'CableTV'} · </span>}
                      {format(new Date(r.createdAt), 'dd MMM yyyy')}
                    </div>
                  </div>
                </div>
                {selected === r._id && (
                  <div style={{ padding: '0 18px 14px', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                    {r.requestedPlan && <p style={{ fontSize: '.85rem' }}>Requested plan: <strong>{r.requestedPlan.name}</strong> — ₹{r.requestedPlan.price}</p>}
                    {r.requestedPackage && <p style={{ fontSize: '.85rem' }}>Requested package: <strong>{r.requestedPackage.name}</strong> — ₹{r.requestedPackage.price}</p>}
                    {r.newAddress?.line1 && <p style={{ fontSize: '.85rem' }}>New address: {[r.newAddress.line1, r.newAddress.area, r.newAddress.city, r.newAddress.pincode].filter(Boolean).join(', ')}</p>}
                    {r.reason && <p style={{ fontSize: '.85rem', marginBottom: 12 }}>{r.reason}</p>}
                    {r.status === 'scheduled' && r.scheduledFor && (
                      <div style={{ background: '#fef3c7', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem', marginBottom: 12 }}>
                        Approved — your package will switch automatically on <strong>{format(new Date(r.scheduledFor), 'd MMM yyyy')}</strong>.
                      </div>
                    )}
                    {r.status === 'superseded' && (
                      <div style={{ background: '#f3f4f6', color: '#4b5563', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem', marginBottom: 12 }}>
                        Replaced by a newer package-change request you submitted afterward.
                      </div>
                    )}
                    {r.signature && (
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Your signature:</p>
                        <img src={r.signature} alt="Your signature" style={{ height: 44, background: '#f9fafb', border: '1px solid var(--border)', borderRadius: 4 }} />
                      </div>
                    )}
                    {['completed', 'rejected'].includes(r.status) && r.reviewNotes && (
                      <div style={{ background: r.status === 'completed' ? '#d1fae5' : '#fee2e2', padding: '10px 14px', borderRadius: 8, fontSize: '.85rem' }}>
                        <strong>Note from our team:</strong> {r.reviewNotes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
